import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, MapPin, Phone, Mail, Instagram, Loader2, Sparkles, CalendarDays } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { TextReveal } from '@/components/TextReveal';
import { SEO } from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { submitContact } from '@/lib/api';
import { SITE_IMAGES, SITE_VIDEOS } from '@/lib/siteImages';
import { CONTACT_PAGE, INTELLIGENCE_ENTRY, BOOK_ENTRY } from '@/constants/testIds';

const EMPTY_FORM = { name: '', email: '', subject: '', message: '' };

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Please enter your name.';
  if (!form.email.trim()) {
    errors.email = 'Please enter your email.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Please enter a valid email address.';
  }
  if (!form.subject.trim()) errors.subject = 'Please enter a subject.';
  if (!form.message.trim()) errors.message = 'Please enter a message.';
  return errors;
}

export default function Contact() {
  const config = useSiteConfig();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setStatus('loading');
    try {
      await submitContact(form);
      setStatus('success');
      setForm(EMPTY_FORM);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      setStatus('error');
      toast.error('Something went wrong. Please try again or email us directly.');
    }
  };

  return (
    <div className="pt-16" data-testid={CONTACT_PAGE.root}>
      <SEO
        title="Contact"
        description="Get in touch with LXRA Studios to book your photography or videography session in the DMV area."
      />
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-4rem)]">
          {/* FORM SIDE */}
          <div className="flex items-center bg-[#0A0A0A] px-4 sm:px-8 lg:px-16 py-20">
            <Reveal className="w-full max-w-lg">
              <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-3">Get In Touch</p>
              <h1 className="font-display text-4xl sm:text-5xl tracking-[0.03em] leading-[0.98] text-[hsl(var(--lxra-fg))]">
                <TextReveal text="LET'S WORK" />
                <br />
                <TextReveal text="TOGETHER." delay={0.08} />
              </h1>
              <div className="mt-5 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
              <p className="mt-6 text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed max-w-md">
                Have a question or ready to book? Send a message and we&apos;ll get back to you as soon as possible.
              </p>
              <Link
                to="/plan"
                data-testid={INTELLIGENCE_ENTRY.contactCta}
                className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold-hover))] transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Planning a session? Start with a Creative Blueprint
              </Link>
              <Link
                to="/book"
                data-testid={BOOK_ENTRY.contactLink}
                className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold-hover))] transition-colors"
              >
                <CalendarDays className="h-3.5 w-3.5" />
                Know your date? Request a session online
              </Link>

              <form onSubmit={handleSubmit} data-testid={CONTACT_PAGE.form} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Your Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={handleChange('name')}
                    placeholder="Jane Doe"
                    data-testid={CONTACT_PAGE.nameInput}
                    className="mt-1.5 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] placeholder:text-[hsl(var(--lxra-muted))]/60 focus-visible:ring-[hsl(var(--lxra-gold))]"
                  />
                  {errors.name && <p className="mt-1.5 text-xs text-[hsl(8,55%,58%)]">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange('email')}
                    placeholder="jane@email.com"
                    data-testid={CONTACT_PAGE.emailInput}
                    className="mt-1.5 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] placeholder:text-[hsl(var(--lxra-muted))]/60 focus-visible:ring-[hsl(var(--lxra-gold))]"
                  />
                  {errors.email && <p className="mt-1.5 text-xs text-[hsl(8,55%,58%)]">{errors.email}</p>}
                </div>

                <div>
                  <Label htmlFor="subject" className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Subject</Label>
                  <Input
                    id="subject"
                    value={form.subject}
                    onChange={handleChange('subject')}
                    placeholder="Engagement session inquiry"
                    data-testid={CONTACT_PAGE.subjectInput}
                    className="mt-1.5 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] placeholder:text-[hsl(var(--lxra-muted))]/60 focus-visible:ring-[hsl(var(--lxra-gold))]"
                  />
                  {errors.subject && <p className="mt-1.5 text-xs text-[hsl(8,55%,58%)]">{errors.subject}</p>}
                </div>

                <div>
                  <Label htmlFor="message" className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Your Message</Label>
                  <Textarea
                    id="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange('message')}
                    placeholder="Tell us about your session, date, and location..."
                    data-testid={CONTACT_PAGE.messageTextarea}
                    className="mt-1.5 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] placeholder:text-[hsl(var(--lxra-muted))]/60 focus-visible:ring-[hsl(var(--lxra-gold))] resize-none"
                  />
                  {errors.message && <p className="mt-1.5 text-xs text-[hsl(8,55%,58%)]">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  data-testid={CONTACT_PAGE.submitButton}
                  className="w-full sm:w-auto inline-flex items-center justify-center h-12 px-7 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] hover:-translate-y-0.5 transition-transform duration-200 text-xs uppercase tracking-[0.1em] font-semibold disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </button>

                <p data-testid={CONTACT_PAGE.statusText} className="text-xs text-[hsl(var(--lxra-muted))] min-h-[1rem]">
                  {status === 'success' && 'Your message has been sent! We\'ll get back to you soon.'}
                  {status === 'error' && 'Something went wrong. Please try again or email us directly.'}
                </p>
              </form>
            </Reveal>
          </div>

          {/* INFO SIDE */}
          <div className="relative bg-[#0F0F10] px-4 sm:px-8 lg:px-16 py-20 flex items-center overflow-hidden">
            <div className="absolute inset-0">
              <video
                className="h-full w-full object-cover opacity-70"
                src={SITE_VIDEOS.contactBackground}
                poster={SITE_IMAGES.contactBackground}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                data-testid="contact-background-video"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F10] via-[#0F0F10]/40 to-[#0F0F10]/10" />
            </div>
            <Reveal delay={0.1} className="relative z-10 w-full max-w-md">
              <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-4">Let&apos;s Connect</p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="h-5 w-5 text-[hsl(var(--lxra-gold))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-[hsl(var(--lxra-fg))]">{config.service_area}</span>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="h-5 w-5 text-[hsl(var(--lxra-gold))] flex-shrink-0 mt-0.5" />
                  <a href={`tel:${config.phone}`} className="text-sm sm:text-base text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] transition-colors">{config.phone}</a>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="h-5 w-5 text-[hsl(var(--lxra-gold))] flex-shrink-0 mt-0.5" />
                  <a href={`mailto:${config.email}`} className="text-sm sm:text-base text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] transition-colors break-all">{config.email}</a>
                </div>
                <div className="flex items-start gap-4">
                  <Instagram className="h-5 w-5 text-[hsl(var(--lxra-gold))] flex-shrink-0 mt-0.5" />
                  <a href={config.instagram_url} target="_blank" rel="noopener noreferrer" className="text-sm sm:text-base text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] transition-colors">{config.instagram_handle}</a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
